import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import isClient from 'commons/isClient';
import isProd from 'commons/isProd';
import requestIdleCallback from 'commons/requestIdleCallback';
const SCRIPT_ID = 'bazaarvoice-script';
const BV_CLIENT = 'joybird';
const BV_SITE_ID = 'main_site';
const BV_LOCALE = 'en_US';
const getBVEnvironment = () => isProd() ? 'production' : 'staging';
const getBVScriptUrl = () => {
  const env = getBVEnvironment();
  return `https://apps.bazaarvoice.com/deployments/${BV_CLIENT}/${BV_SITE_ID}/${env}/${BV_LOCALE}/bv.js`;
};

// pendingCallbacks: null = no load in progress; array = load in progress.
const bvScriptState = {
  pendingCallbacks: null
};

// Fired when rating-summary stars request the reviews widget (e.g. before
// intersection-gated mount). PDPCustomerPhotos subscribes to force-mount.
const bvShowReviewsListeners = new Set();
export const onBvShowReviews = listener => {
  bvShowReviewsListeners.add(listener);
  return () => bvShowReviewsListeners.delete(listener);
};
let bvCallbackInstalled = false;

// BV shadow-DOM v2 apps injected after page load require registerMutationObserver
// (BV docs). swat_reviews.on('show') is required when reviews are not yet in the DOM
// (e.g. behind a tab or intersection-gated).
const ensureBvCallback = () => {
  if (!isClient() || bvCallbackInstalled) return;
  bvCallbackInstalled = true;
  const previousCallback = window.bvCallback;
  window.bvCallback = BV => {
    if (typeof BV?.registerMutationObserver === 'function') {
      BV.registerMutationObserver();
    }
    if (typeof BV?.swat_reviews?.on === 'function') {
      BV.swat_reviews.on('show', () => {
        bvShowReviewsListeners.forEach(listener => listener());
      });
    }
    if (typeof previousCallback === 'function') {
      previousCallback(BV);
    }
  };
};
const loadBVScript = callback => {
  if (!isClient()) return;
  ensureBvCallback();
  if (bvScriptState.pendingCallbacks !== null) {
    if (callback) bvScriptState.pendingCallbacks.push(callback);
    return;
  }
  if (!document.getElementById(SCRIPT_ID)) {
    // Set synchronously so concurrent callers queue instead of injecting twice; defer
    // only the injection to idle, keeping bv.js off the critical path (CON-8284).
    bvScriptState.pendingCallbacks = callback ? [callback] : [];
    requestIdleCallback(() => {
      // a remount may have injected during the idle gap
      if (document.getElementById(SCRIPT_ID)) return;
      const script = document.createElement('script');
      script.src = getBVScriptUrl();
      script.id = SCRIPT_ID;
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        const cbs = bvScriptState.pendingCallbacks || [];
        bvScriptState.pendingCallbacks = null;
        cbs.forEach(cb => cb());
      };
      script.onerror = () => {
        bvScriptState.pendingCallbacks = null;
        const failedScript = document.getElementById(SCRIPT_ID);
        if (failedScript) failedScript.remove();
      };
    });
  } else if (callback) {
    callback();
  }
};
const useBazaarVoice = () => {
  const $ = _c(4);
  const [isReady, setIsReady] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      let isCancelled = false;
      if (document.getElementById(SCRIPT_ID) && bvScriptState.pendingCallbacks === null) {
        const timer = setTimeout(() => {
          if (!isCancelled) {
            setIsReady(true);
          }
        }, 300);
        return () => {
          isCancelled = true;
          clearTimeout(timer);
        };
      }
      loadBVScript(() => {
        if (!isCancelled) {
          setIsReady(true);
        }
      });
      return () => {
        isCancelled = true;
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== isReady) {
    t2 = {
      isReady
    };
    $[2] = isReady;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export default useBazaarVoice;